<template>
  <CustomTitleBar title="上传身份证" />
  <div class="page-content">
    <!-- 身份证上传区域 -->
    <div class="upload-area">
      <!-- 人像面上传 -->
      <div class="upload-item">
        <div class="upload-title">拍摄人像面</div>
        <div class="upload-box">
          <van-uploader v-model="frontFileList" :max-count="1" :before-read="beforeRead" @delete="onDelete('front')"
            :after-read="afterRead" name="front" :preview-size="170" :preview-image="true">
            <template v-if="frontImage">
              <img :src="frontImage" class="preview-image" />
            </template>
            <template v-else>
              <div class="default-box">
                <img src="@/assets/images/sfz1.png" class="sfz-icon" />
              </div>
            </template>
          </van-uploader>
        </div>
      </div>
      <!-- 国徽面上传 -->
      <div class="upload-item">
        <div class="upload-title">拍摄国徽面</div>
        <div class="upload-box">
          <van-uploader v-model="backFileList" :max-count="1" :before-read="beforeRead" @delete="onDelete('back')"
            :after-read="afterRead" name="back" :preview-size="170" :preview-image="true">
            <template v-if="backImage">
              <img :src="backImage" class="preview-image" />
            </template>
            <template v-else>
              <div class="default-box">
                <img src="@/assets/images/sfz2.png" class="sfz-icon" />
              </div>
            </template>
          </van-uploader>
        </div>
      </div>
    </div>
    <!-- 上传按钮 -->
    <div class="submit-btn">
      <van-button type="primary" block round @click="handleSubmit" :disabled="!frontImage || !backImage">
        上传
      </van-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CustomTitleBar from "@/components/CustomTitleBar/index.vue";
import { ref } from "vue";
import { showToast } from 'vant';
import { useRouter } from 'vue-router';
import type { UploaderFileListItem, UploaderBeforeRead, UploaderAfterRead } from 'vant';
import { uploadFile } from '@/api/system';
import { uploadSfz } from '@/api/wallet';

const router = useRouter();
const frontImage = ref('');
const backImage = ref('');
const frontFileList = ref<UploaderFileListItem[]>([]);
const backFileList = ref<UploaderFileListItem[]>([]);

// 添加图片压缩函数
const compressImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // 设置压缩后的尺寸（建议最大宽度为1024）
        const maxWidth = 1024;
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (maxWidth * height) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        // 转换为 blob，压缩质量为 0.8
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          } else {
            reject(new Error('压缩失败'));
          }
        }, 'image/jpeg', 0.8);
      };
    };
    reader.onerror = reject;
  });
};

// 修改上传前校验
const beforeRead: UploaderBeforeRead = (file) => {
  const currentFile = Array.isArray(file) ? file[0] : file;
  const isImage = currentFile.type.includes('image');
  if (!isImage) {
    showToast('请上传图片格式文件');
    return false;
  }

  // 检查文件大小（例如：限制为 10MB）
  const maxSize = 10 * 1024 * 1024;
  if (currentFile.size > maxSize) {
    showToast('图片大小不能超过10MB');
    return false;
  }

  return true;
};

// 修改上传后处理
const afterRead: UploaderAfterRead = async (file, detail) => {
  const currentFile = Array.isArray(file) ? file[0] : file;
  try {
    // 压缩图片
    const compressedFile = await compressImage(currentFile.file as File);

    // 生成新的文件名
    const timestamp = new Date().getTime();
    const fileExt = compressedFile.name.split('.').pop();
    const newFileName = `sfz_${detail.name}.${fileExt}`;

    // 创建新的 File 对象，使用新文件名
    const renamedFile = new File([compressedFile], newFileName, {
      type: compressedFile.type
    });

    const formData = new FormData();
    formData.append('file', renamedFile);

    const res = await uploadFile(formData);
    if (detail.name === 'front') {
      frontImage.value = res.data;
    } else {
      backImage.value = res.data;
    }
  } catch (error) {
    showToast('上传失败，请重试');
  }
};

// 删除图片
const onDelete = (type: 'front' | 'back') => {
  if (type === 'front') {
    frontImage.value = '';
  } else {
    backImage.value = '';
  }
};

// 提交表单
const handleSubmit = async () => {
  if (!frontImage.value || !backImage.value) {
    showToast('请上传身份证正反面照片');
    return;
  }
  // 处理提交逻辑
  console.log(frontImage.value, backImage.value);
  try {
    const res = await uploadSfz({
      frontPicCode: frontImage.value,
      backPicCode: backImage.value
    });
    console.log(res);
    if (res.code === 200) {
      showToast('上传成功');
      router.go(-1);
    } else {
      showToast(res.msg);
    }
  } catch (error) {
    console.log(error);
  }
};
</script>

<style lang="scss" scoped>
.page-content {
  padding: 44px 16px 0;
  background: #f5f5f5;
}

// 上传区域样式
.upload-area {
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
}

// 上传项样式
.upload-item {
  margin-bottom: 20px;

  &:last-child {
    margin-top: 40px;
    margin-bottom: 0;
  }
}

// 上传标题
.upload-title {
  margin-top: 25px;
  margin-bottom: 12px;
  font-size: 15px;
  color: #9E9E9E;
  text-align: center;
}

// 上传框
.upload-box {
  width: 100%;
  height: 170px;
  border-radius: 8px;
  overflow: hidden;

  :deep(.van-uploader) {
    width: 100%;
    height: 100%;
  }

  :deep(.van-uploader__wrapper) {
    width: 100%;
    height: 100%;
    justify-content: center;
  }

  :deep(.van-uploader__preview) {
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
  }

  :deep(.van-uploader__preview-image) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }

  .preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .default-box {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .sfz-icon {
      width: 100%;
      height: 170px;
      object-fit: contain;
    }
  }
}

// 提交按钮
.submit-btn {
  margin-top: 24px;
  padding: 0 16px;
  display: flex;
  justify-content: center;

  :deep(.van-button) {
    width: 70%;
    height: 44px;
    font-size: 16px;
    background: #1989fa;
  }
}
</style>